import type { Editor } from '@tiptap/react';
import { ReactRenderer } from '@tiptap/react';
import TiptapSuggestion from '@tiptap/suggestion';
import { PluginKey } from 'prosemirror-state';
import tippy from 'tippy.js';

import type { Mention, MentionListProps, MentionListRef } from '../types';
import MentionList from './MentionList';

export const Suggestion = (
  editor: Editor,
  {
    mentionTrigger,
    getMentions,
    visibleItemsBeforeOverflow,
    handleDropdownOpen,
    handleDropdownClose,
    supportWhitespace = true,
    zIndex,
  }: {
    mentionTrigger: string;
    getMentions: (query: string) => Promise<Mention[]>;
    visibleItemsBeforeOverflow: number;
    handleDropdownOpen?: () => void;
    handleDropdownClose?: () => void;
    supportWhitespace?: boolean;
    zIndex: number;
  }
) => {
  const shouldEndMentioningProcess = (query: string, items: Mention[]) => {
    //There is no way to programmatically end mentioning process
    //https://github.com/ueberdosis/tiptap/issues/823
    const names = items.map(item => item.name.toLowerCase());
    return !names.some(name => name.includes(query.toLowerCase()));
  };

  return TiptapSuggestion({
    editor,
    char: mentionTrigger,
    allowSpaces: supportWhitespace,
    pluginKey: new PluginKey('mention'),
    command: ({
      editor,
      range,
      props,
    }: {
      editor: Editor;
      range: { from: number; to: number };
      props: Mention;
    }) => {
      const { name, slug, id } = props;
      editor.chain().focus().insertMention({ name, slug, id }, range).run;
    },
    items: async ({ query }) => {
      const mentions = await getMentions(query);
      return mentions
        .map(mention => ({
          name: mention.name,
          slug: mention.slug,
          avatar: mention.avatar,
          id: mention.id,
        }))
        .slice(0, visibleItemsBeforeOverflow);
    },
    render: () => {
      let component: ReactRenderer<
        MentionListRef,
        MentionListProps & React.RefAttributes<MentionListRef>
      >;
      let popup: ReturnType<typeof tippy>;

      return {
        onStart: props => {
          component = new ReactRenderer(MentionList, {
            editor,
            props,
          });

          component.element.setAttribute('dir', '');
          if (handleDropdownOpen) {
            handleDropdownOpen();
          } else {
            const portal = document.querySelector('[data-hook=ricos-portal]') ?? document.body;
            popup = tippy('body', {
              getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(),
              appendTo: () => portal,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
              zIndex,
            });
          }
        },

        onUpdate(props) {
          component.updateProps(props);

          if (!handleDropdownOpen) {
            popup[0].setProps({
              getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(),
            });
          }
        },

        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            // @ts-ignore $TSFixMe
            this.onExit?.();
            return true;
          }

          const { items, query } = component.props;
          if (props.event.key === 'Enter' && shouldEndMentioningProcess(query, items)) {
            // @ts-ignore $TSFixMe
            this.onExit?.();
            return false;
          }

          if (!handleDropdownClose) {
            return component.ref?.onKeyDown?.(props) ?? false;
          }
          return false;
        },

        onExit() {
          component?.destroy?.();
          if (handleDropdownClose) {
            handleDropdownClose();
          } else {
            popup[0].destroy();
          }
        },
      };
    },
  });
};
